/**
 * Hook that tracks page views on route changes via React Router.
 * Derives a human-readable page name from the URL path.
 */

import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const PAGE_NAME_MAP: Record<string, string> = {
  'sign-in': 'Sign In',
  'sign-up': 'Sign Up',
  onboarding: 'Onboarding',
  chat: 'Chat',
  settings: 'Settings',
  general: 'General Settings',
  members: 'Members Settings',
  balance: 'Balance Settings',
  'payment-methods': 'Payment Methods',
  subscription: 'Subscription Settings',
  invoices: 'Invoices',
  'usage-audit': 'Usage Audit',
  profile: 'Profile',
  agencies: 'Agencies',
  explore: 'Agencies Explore',
  studio: 'Agencies Studio',
  organization: 'Agencies Organization',
  templates: 'Agency Templates',
  new: 'Create Agency',
  edit: 'Edit Agency',
}

const resolvePageName = (pathname: string): string => {
  const segmentList = pathname.split('/').filter(Boolean)
  for (let i = segmentList.length - 1; i >= 0; i--) {
    const match = PAGE_NAME_MAP[segmentList[i]]
    if (match) return match
  }
  return 'Unknown Page'
}

export const usePageTracking = (): void => {
  const location = useLocation()
  const lastTrackedPathRef = useRef<string | null>(null)

  useEffect(() => {
    if (lastTrackedPathRef.current === location.pathname) return
    lastTrackedPathRef.current = location.pathname

    trackEvent('page_viewed', {
      pageName: resolvePageName(location.pathname),
      path: location.pathname,
    })
  }, [location.pathname])
}
